import { useQuery } from '@tanstack/react-query';
import { Pencil } from 'lucide-react';
import { Link } from 'react-router';
import { StatusBadge } from '../../components/ui/Badge.js';
import { Button } from '../../components/ui/Button.js';
import { type Column, DataTable } from '../../components/ui/DataTable.js';
import { ListSearch } from '../../components/ui/ListSearch.js';
import { PageHeader } from '../../components/ui/PageHeader.js';
import { RowActions } from '../../components/ui/RowActions.js';
import { useAbility } from '../../context/AbilityContext.js';
import { useListControls } from '../../hooks/useListControls.js';
import { api } from '../../services/api.js';
import { RESOURCE_KEYS } from '../../services/resourceKeys.js';

/** One screenful. Matches the other paginated admin lists rather than inventing a third size. */
const PAGE_SIZE = 20;

interface Supplier {
  _id: string;
  company_name: string;
  email?: string;
  phone?: string;
  status: string;
}

interface SuppliersResponse {
  items: Supplier[];
}

export function SupplierListPage() {
  const ability = useAbility();
  // 162 (#21): a pager and a search box on a list that had neither.
  const { page, setPage, searchInput, setSearchInput, search } = useListControls();

  const listPath = (() => {
    const params = new URLSearchParams({ page: String(page), limit: String(PAGE_SIZE) });
    // Only when there is something to filter by: a cleared box must show the whole list, not an
    // empty one (FR-004).
    if (search) params.set('q', search);
    return `/suppliers?${params.toString()}`;
  })();

  const { data, isLoading, error } = useQuery({
    queryKey: [...RESOURCE_KEYS.supplier.list, page, search],
    queryFn: () => api.get<SuppliersResponse & { pages: number }>(listPath),
    // 063 (FR-010): a refusal is not a transient fault. Without this the caller
    // reads the empty state through three retries before the truth arrives.
    retry: false,
  });

  const columns: Column<Supplier>[] = [
    { key: 'company_name', header: 'Company Name' },
    { key: 'email', header: 'Email', render: (s) => s.email ?? '—' },
    { key: 'phone', header: 'Phone', render: (s) => s.phone ?? '—' },
    {
      key: 'status',
      header: 'Status',
      render: (s) => <StatusBadge status={s.status} />,
    },
    {
      key: '_id',
      header: 'Actions',
      render: (s) => (
        <RowActions
          actions={[
            {
              key: 'edit',
              label: 'Edit',
              href: `/suppliers/${s._id}`,
              icon: <Pencil className="h-4 w-4" />,
            },
          ]}
        />
      ),
    },
  ];

  return (
    <div>
      <PageHeader
        title="Suppliers"
        description="Supplier organizations — contacts and status."
        actions={
          ability.can('manage', 'Supplier') && (
            <Link to="/suppliers/new">
              <Button>+ New Supplier</Button>
            </Link>
          )
        }
      />
      <ListSearch
        value={searchInput}
        onChange={setSearchInput}
        label="Search suppliers by company name"
        testId="supplier-search"
      />
      <DataTable
        columns={columns}
        data={data?.items ?? []}
        loading={isLoading}
        keyExtractor={(r) => r._id}
        emptyMessage="No suppliers yet"
        // 063: this endpoint demands `manage Supplier` and 403s a provider. Two
        // organisations exist on staging, and they were told there were none.
        error={error as { status?: number } | null}
        pagination={{
          page: page,
          totalPages: data?.pages ?? 1,
          onPageChange: setPage,
        }}
      />
    </div>
  );
}
